import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Home, Car, Settings, Store } from 'lucide-react';
import NotificationBell from './NotificationBell';
import Sidebar from './Sidebar';
import usePageTracking from '../hooks/usePageTracking';
import SubscriptionBanner from './SubscriptionBanner';

const Layout = () => {
  usePageTracking();
  useAuth();
  const location = useLocation();

  // Mobile navigation items (bottom tabs — unchanged from current behavior)
  const mobileNav = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Mon stock', href: '/stock', icon: Car },
    { name: 'Paramètres', href: '/settings', icon: Settings },
    { name: 'Marketplace', href: '/marketplace', icon: Store, highlight: true },
  ];

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Desktop sidebar — hidden on mobile */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Mobile header with logo + notification bell */}
      <header className="md:hidden bg-dark-surface border-b border-dark-border">
        <div className="flex justify-between items-center h-14 px-4">
          <Link to="/dashboard" className="flex items-center">
            <div className="w-7 h-7 rounded-lg bg-carmin flex items-center justify-center">
              <span className="text-white font-bold text-xs">C</span>
            </div>
            <span className="ml-2 text-lg font-bold text-primary">Carmin</span>
          </Link>
          <div className="flex items-center gap-2">
            <NotificationBell />
          </div>
        </div>
      </header>

      {/* Mobile bottom navigation — same as current */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-dark-surface border-t border-dark-border z-40">
        <div className="flex justify-around py-2">
          {mobileNav.map((item) =>
            item.highlight ? (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                  isActive(item.href)
                    ? 'bg-accent-purple text-white shadow-md shadow-accent-purple/30'
                    : 'bg-accent-purple/80 text-white'
                }`}
              >
                <item.icon className="h-5 w-5" />
                <span className="mt-1">{item.name}</span>
              </Link>
            ) : (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center px-3 py-2 text-xs ${
                  isActive(item.href) ? 'text-carmin' : 'text-muted'
                }`}
              >
                <item.icon className="h-5 w-5" />
                <span className="mt-1">{item.name}</span>
              </Link>
            )
          )}
        </div>
      </nav>

      <SubscriptionBanner />

      {/* Main content — offset by sidebar width on desktop, pad bottom for mobile tabs */}
      <main className="sidebar-content md:px-8 px-4 py-8 pb-24 md:pb-8">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
